/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useMsal} from '@azure/msal-react';
import React, {useEffect, useState} from "react";
import {checkResponse} from "../auth/authConfig";
import Header, {shouldShowHeader} from "../containers/components/Header/Header";
import Sidebar from "../containers/components/Sidebar/Sidebar";
import {theme} from "../constants/theme";
import {Toaster} from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import useGetCurrentPath from "../hooks/useGetCurrentPath";
import GlobalDialog from "../containers/components/Global/Dialog/GlobalDialog";
import GlobalRightClickMenu from "../containers/components/Global/GlobalRightClickMenu";
import {useAppDispatch, useAppSelector} from "../redux/hooks";
import {setGlobalRightClickMenuPosition} from "../redux/ducks/GlobalRightClickMenu/slice";
import {selectCurrentUser, setUser} from "../redux/ducks/AccessControl/slice";
import NotFound from '../containers/pages/navigation/NotFound';
import {usePostRequest, useReadWriteFavorites} from "../api/helper";
import {useHasAccessToCurrentRoute} from "../redux/ducks/AccessControl/accessControlHelpers";
import {setBenchmarkData} from '../redux/ducks/CoverageRequirements/slice';
import {setIsLoadingTrackedItems, setTrackedItems} from "../redux/ducks/Favorites/slice";
import {useFetchToken} from "../api/helper";
import {fetchAdasPrograms} from '../redux/ducks/AdasPrograms';
import {fetchUsers} from "../redux/ducks/Users";
import useGetHubName from '../containers/components/Socket/useGetHubName';
import useSocket from '../containers/components/Socket/useSocket';
import { useCallback } from "react";
import { SocketMessage as SensingArchSocketMessage, SocketMessageTypes as SensingArchSocketMessageTypes} from "../containers/pages/SensingSystemArchitectureDesign/types";
import { SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS } from "../containers/pages/SensingSystemArchitectureDesign/constants";
import {
  addStudy as SensingArchAddStudy,
  deleteStudy as SensingArchDeleteStudy,
  fetchStudies as fetchArchitectureStudies,
  Experiment as SensingArchExperiment,
  Study as SensingArchStudy,
  updateStudy as SensingArchUpdateStudy,
  updateExperiment as SensingArchUpdateExperiment, setGreenlineIdToProperties, setGreenlineIdToPlotData, setGreenlineIdToRunState, setSensorIdToBluelineRunState, setSensorIdToViolationsRunState
} from "../redux/ducks/SensingSystemArchitectureDesign"
import {decodeAndDecompress} from "../containers/pages/CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/LeftPanelCoverageRequirements";


const useShouldShowHeaderTitle = () => {
  const currentPath = useGetCurrentPath()
  const showHeader = shouldShowHeader(currentPath);
  return showHeader
}

export default function Link({componentSub}: any) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const {instance, accounts} = useMsal();
  const [loginBool, setLoginBool] = useState(false);
  const fetchHandle = usePostRequest('/HttpTriggerAuthorizeCheck', false)

  const currentUser = useAppSelector(state => selectCurrentUser(state))

  const fetchToken = useFetchToken()

  /** Fetch users on initial page load */
  useEffect(() => {
    const fetchUsersData = async () => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(fetchUsers(idToken))
    }
    fetchUsersData()
  }, [])

  /** Fetch ADAS Programs on initial page load */
  useEffect(() => {
    const fetchAdasProgramsData = async () => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(fetchAdasPrograms(idToken))
    }
    fetchAdasProgramsData()
  }, [])



  /**
   * Fetch favorites here and store in redux, because we fetch the user here (and we need the gmid),
   * don't want to rerender at top level for that
   */
  const [favorites, _] = useReadWriteFavorites(true)

  useEffect(() => {
    fetchData();
  }, [])

  const fetchTrackedItemsSearchData = usePostRequest('/HttpTriggerTrackedItems')
  useEffect(() => {
    if (!currentUser || !currentUser.id?.length) return
    const body = {
      requestType: 'search',
      gmid: currentUser.id
    }
    dispatch(setIsLoadingTrackedItems(true))
    fetchTrackedItemsSearchData(body).then((response) => {
      dispatch(setTrackedItems(response))
    }).catch(err => {
      console.error(err)
    }).finally(() => {
      dispatch(setIsLoadingTrackedItems(false))
    })
  }, [currentUser])

  const fetchData = async () => {
    fetchHandle().then((response) => {
        checkResponse(instance, response, false);
        if (response.status === 403) {
          console.error('403', response.status);
          setLoginBool(false);
          navigate("/requestaccess")
        } else {
          setLoginBool(true);
          return response.json();
        }
      }
    ).then((data) => {
      dispatch(setUser(data))
    }).catch(err => {
      console.error(err)
      navigate("/notfound")
    });
  }

  /** Set up the Sensing System Architecture Design socket message handler */
  const onSensingArchSocketMessage = useCallback((msg: SensingArchSocketMessage) => {
    let newMsg = msg
    if (typeof msg === 'string') {
      try {
        newMsg = decodeAndDecompress(msg)
      } catch (err) {
        console.error("Failed to decode and decompress socket message:", err);
        return; // Early exit if parsing fails
      }
    }
    const type = newMsg.type
    const data = newMsg.data

    switch (type) {
      case SensingArchSocketMessageTypes.ADD_STUDY: {
        dispatch(SensingArchAddStudy(data as SensingArchStudy))
        break
      }
      case SensingArchSocketMessageTypes.UPDATE_STUDY: {
        dispatch(SensingArchUpdateStudy(data as SensingArchStudy))
        break
      }
      case SensingArchSocketMessageTypes.DELETE_STUDY: {
        dispatch(SensingArchDeleteStudy(data as string))
        break
      }
      case SensingArchSocketMessageTypes.UPDATE_EXPERIMENT: {
        dispatch(SensingArchUpdateExperiment(data as SensingArchExperiment))
        break
      }
      case SensingArchSocketMessageTypes.SET_GREENLINE_ID_TO_PROPERTIES: {
        dispatch(setGreenlineIdToProperties(data))
        break
      }
      case SensingArchSocketMessageTypes.SET_GREENLINE_ID_TO_PLOT_DATA: {
        dispatch(setGreenlineIdToPlotData(data))
        break
      }
      case SensingArchSocketMessageTypes.SET_GREENLINE_ID_TO_RUN_STATE: {
        dispatch(setGreenlineIdToRunState(data))
        break
      }
      case SensingArchSocketMessageTypes.SET_SENSOR_ID_TO_BLUELINE_RUN_STATE: {
        dispatch(setSensorIdToBluelineRunState(data))
        break
      }
      case SensingArchSocketMessageTypes.SET_SENSOR_ID_TO_VIOLATIONS_RUN_STATE: {
        dispatch(setSensorIdToViolationsRunState(data))
        break
      }
    }
  }, [])

  /** Set up the Sensing System Architecture Design socket connection */
  const sensingArchHubName = useGetHubName(SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.app.getSocketName())
  useSocket(sensingArchHubName, onSensingArchSocketMessage)


  const hasAccessToCurrentRoute = useHasAccessToCurrentRoute()
  const shouldShowBlank = currentUser == null || (Object.keys(currentUser)?.length === 0 && !hasAccessToCurrentRoute)
  const pageHeight = useShouldShowHeaderTitle() ? `calc(100vh - ${theme.layout.header.height})` : '100vh'
  return (
    <>
      {
        !shouldShowBlank &&
        <div style={{display: 'flex', width: "100vw", height: '100vh', overflow: 'auto'}}
             onClick={() => {
               dispatch(setGlobalRightClickMenuPosition(null))
             }}>
          {
            !hasAccessToCurrentRoute ? <NotFound/>
              : <div style={{display: "flex", width: "100%", overflow: 'auto', height: '100%'}}>
                {loginBool &&
                  <div>
                    <Sidebar/>
                  </div>
                }
                <div style={{flexGrow: 1, overflow: 'auto', height: '100%'}}>
                  {loginBool && <Header/>}
                  <div style={{overflow: 'auto', height: pageHeight}}>
                    {loginBool && componentSub}
                  </div>
                </div>
              </div>
          }
          <Toaster position={'top-right'}/>
          <GlobalDialog/>
          <GlobalRightClickMenu/>
        </div>
      }
    </>
  )

}